import classNames from 'classnames';
import React, {ComponentPropsWithRef, forwardRef, useImperativeHandle, useRef} from 'react';
import {ButtonColors} from 'src/types/store';
import NewTabTooltip from 'shared/components/new-tab-tooltip';
import 'stylesheets/components/shared-button.scss';

export interface Props extends ComponentPropsWithRef<'button'> {
  // Color of the button.
  // Deprecated values: primary, alternate, and negative
  // use blue, white, and red respectively.
  color?: ButtonColors;
  buttonName?: string;
  margin?: boolean;
  horizontalMargin?: boolean;
  outline?: boolean;
  link?: boolean;
  width?: string | number;
  fixedWidth?: string | number;
  size?: 'tiny' | 'small' | 'medium' | 'large';
  cy?: string;
  testId?: string;
  newTabTooltip?: boolean;
}

const Button = forwardRef<HTMLButtonElement, Props>(function Button(
  {
    className,
    color = 'blue',
    buttonName,
    margin = true,
    horizontalMargin,
    outline,
    link,
    width = 0,
    fixedWidth,
    size = 'medium',
    cy,
    testId,
    children,
    newTabTooltip,
    onClick,
    ...buttonProps
  },
  forwardedRef
) {
  const ref = useRef<HTMLButtonElement>(null);

  // if forwarded a ref, expose existing button ref
  useImperativeHandle(forwardedRef, () => ref.current!, []);

  let styleWidth: {minWidth?: string | number; width?: string | number} = {minWidth: width};

  if (fixedWidth) {
    styleWidth = {width: fixedWidth};
  }

  const buttonComponent = (
    <button
      ref={ref}
      className={classNames(
        'shared-button',
        className,
        size,
        `${color}${outline ? '-outline' : ''}${link ? '-link' : ''}`,
        {margin, 'horizontal-margin': horizontalMargin}
      )}
      type="button"
      onClick={(e) => {
        if (buttonProps['aria-disabled']) {
          e.preventDefault();
          return;
        }

        onClick?.(e);
      }}
      style={styleWidth}
      data-segment-track={buttonName && 'button'}
      data-button-name={buttonName}
      data-cy={cy}
      data-testid={testId}
      {...buttonProps}
    >
      {children}
    </button>
  );

  if (newTabTooltip) {
    return <NewTabTooltip>{buttonComponent}</NewTabTooltip>;
  } else {
    return buttonComponent;
  }
});

export default Button;
